import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMail, FiLock, FiArrowRight, FiCopy } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const PARTICLES = Array.from({ length: 15 }, () => ({
  size: 4 + Math.random() * 4,
  opacity: 0.1 + Math.random() * 0.2,
  left: `${Math.random() * 100}%`,
  duration: 15 + Math.random() * 20,
  delay: Math.random() * 10,
}));

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #064e3b 0%, #047857 30%, #059669 60%, #34d399 100%)',
      backgroundSize: '400% 400%',
      animation: 'gradient-shift 15s ease infinite',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Animated background elements */}
      <div style={{
        position: 'absolute', width: 500, height: 500, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(167,243,208,0.15) 0%, transparent 70%)',
        top: -100, right: -100, animation: 'float 20s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', width: 700, height: 700, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(110,231,183,0.1) 0%, transparent 70%)',
        bottom: -200, left: -200, animation: 'float 25s ease-in-out infinite reverse',
      }} />
      <div style={{
        position: 'absolute', width: 300, height: 300, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(52,211,153,0.12) 0%, transparent 70%)',
        top: '30%', left: '20%', animation: 'float 18s ease-in-out infinite 3s',
      }} />

      {/* Floating particles */}
      {PARTICLES.map((p, i) => (
        <div key={i} style={{
          position: 'absolute', width: p.size, height: p.size,
          borderRadius: '50%', background: `rgba(167, 243, 208, ${p.opacity})`,
          left: p.left, bottom: '-10px',
          animation: `particle-float ${p.duration}s linear infinite`,
          animationDelay: `${p.delay}s`,
        }} />
      ))}

      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{
          width: '100%', maxWidth: 440, padding: '0 20px',
          position: 'relative', zIndex: 2,
        }}
      >
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{ textAlign: 'center', marginBottom: 32 }}
        >
          <img
            src="/ems-logo.png"
            alt="EMS Logo"
            style={{
              width: 90, height: 90, margin: '0 auto 12px',
              display: 'block', objectFit: 'contain',
              filter: 'drop-shadow(0 4px 16px rgba(52, 211, 153, 0.4))',
            }}
          />
          <h1 style={{
            fontFamily: 'Poppins, sans-serif', fontSize: 28, fontWeight: 800,
            color: 'white', marginBottom: 4,
          }}>
            Export Software
          </h1>
          <p style={{ color: 'rgba(167, 243, 208, 0.8)', fontSize: 14, fontWeight: 400 }}>
            Export Management Software
          </p>
        </motion.div>

        {/* Login Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: 24,
            padding: '40px 32px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
          }}
        >
          <h2 style={{
            fontFamily: 'Poppins, sans-serif', fontSize: 22, fontWeight: 700,
            color: '#064e3b', marginBottom: 8, textAlign: 'center',
          }}>
            Welcome Back
          </h2>
          <p style={{ color: '#6b7280', fontSize: 14, textAlign: 'center', marginBottom: 28 }}>
            Sign in to your account
          </p>

          {/* Demo Credentials */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.35 }}
            style={{
              background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
              border: '1px solid rgba(16, 185, 129, 0.25)',
              borderRadius: 14,
              padding: '14px 16px',
              marginBottom: 24,
              position: 'relative',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 10,
            }}>
              <span style={{
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: 1, color: '#047857',
              }}>
                Demo Credentials
              </span>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="button"
                onClick={() => {
                  setEmail('super-admin@gmail.com');
                  setPassword('BetaZen@2023');
                  toast.success('Credentials filled!');
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '5px 12px', border: 'none', borderRadius: 8,
                  background: 'linear-gradient(135deg, #059669, #10b981)',
                  color: 'white', fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
                }}
              >
                <FiCopy size={12} /> Auto Fill
              </motion.button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiMail size={13} color="#059669" />
                <span style={{ fontSize: 13, color: '#065f46', fontFamily: 'monospace', fontWeight: 500 }}>
                  super-admin@gmail.com
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiLock size={13} color="#059669" />
                <span style={{ fontSize: 13, color: '#065f46', fontFamily: 'monospace', fontWeight: 500 }}>
                  BetaZen@2023
                </span>
              </div>
            </div>
          </motion.div>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'block', fontSize: 13, fontWeight: 600,
                color: '#047857', marginBottom: 8, textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}>
                Email Address
              </label>
              <div style={{ position: 'relative' }}>
                <FiMail size={18} style={{
                  position: 'absolute', left: 14, top: '50%',
                  transform: 'translateY(-50%)', color: '#059669',
                }} />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  style={{
                    width: '100%', padding: '14px 14px 14px 44px',
                    border: '2px solid rgba(16, 185, 129, 0.2)',
                    borderRadius: 12, fontSize: 15, outline: 'none',
                    transition: 'all 0.2s ease', background: '#f0fdf4',
                    fontFamily: 'Inter, sans-serif',
                  }}
                  onFocus={e => {
                    e.target.style.borderColor = '#10b981';
                    e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.15)';
                  }}
                  onBlur={e => {
                    e.target.style.borderColor = 'rgba(16, 185, 129, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 28 }}>
              <label style={{
                display: 'block', fontSize: 13, fontWeight: 600,
                color: '#047857', marginBottom: 8, textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <FiLock size={18} style={{
                  position: 'absolute', left: 14, top: '50%',
                  transform: 'translateY(-50%)', color: '#059669',
                }} />
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  style={{
                    width: '100%', padding: '14px 14px 14px 44px',
                    border: '2px solid rgba(16, 185, 129, 0.2)',
                    borderRadius: 12, fontSize: 15, outline: 'none',
                    transition: 'all 0.2s ease', background: '#f0fdf4',
                    fontFamily: 'Inter, sans-serif',
                  }}
                  onFocus={e => {
                    e.target.style.borderColor = '#10b981';
                    e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.15)';
                  }}
                  onBlur={e => {
                    e.target.style.borderColor = 'rgba(16, 185, 129, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '14px 24px',
                background: 'linear-gradient(135deg, #059669, #10b981)',
                color: 'white', border: 'none', borderRadius: 12,
                fontSize: 16, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
                opacity: loading ? 0.7 : 1,
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              {loading ? (
                <div style={{
                  width: 20, height: 20, border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: 'white', borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
              ) : (
                <>Sign In <FiArrowRight size={18} /></>
              )}
            </motion.button>
          </form>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          style={{
            textAlign: 'center', marginTop: 24,
            color: 'rgba(167, 243, 208, 0.6)', fontSize: 13,
          }}
        >
          Export Management Software v2.0
        </motion.p>
      </motion.div>
    </div>
  );
}
